import { AssessmentMetadata, DescriptionItem, TcoAssessment } from '@amzn/awscat-portfolio-assessment-types';
import { createContext, FC, useState } from 'react';

export interface ITcoContext {
    // assessment will always hold the value stored in the service
    assessment?: TcoAssessment,
    // assessmentToUpdate will be the interim assessment between load and calculate operations to reduce number unnecessary of network calls
    assessmentToUpdate?: TcoAssessment,
    assessmentMetadatas: AssessmentMetadata[];
    assessmentDescription: DescriptionItem[];
    tcoLoading: boolean;
    // only updates the local assessment, makes no attempt to save it. 
    setLocalAssessment: (assessment: TcoAssessment) => void;
    // updateAssessment should only be called when the assessment is loaded from the service.
    updateAssessment: (assessment: TcoAssessment) => void;
    // updateAssessmentToUpdate should be called anytime we want to save changes to the assessment but not trigger a TCO calculate right away
    updateAssessmentToUpdate: (assessment: TcoAssessment) => void;
    updateAssessmentMetadatas: (metadatas: AssessmentMetadata[]) => void;
    updateAssessmentDescription: (description: DescriptionItem[]) => void;
    updateTcoLoading: (loading: boolean) => void;
}

export const TcoContext = createContext<ITcoContext>({
    assessmentMetadatas: [],
    assessmentDescription: [],
    tcoLoading: false,
    setLocalAssessment: () => {},
    updateAssessment: () => {},
    updateAssessmentToUpdate: () => {},
    updateAssessmentMetadatas: () => {},
    updateAssessmentDescription: () => {},
    updateTcoLoading: () => {},
});

export const TcoProvider: FC = ({ children }) => {
    const [ assessment, setAssessment ] = useState<TcoAssessment>();
    const [ assessmentToUpdate, setAssessmentToUpdate ] = useState<TcoAssessment>();
    const [ assessmentMetadatas, setAssessmentMetadatas ] = useState<AssessmentMetadata[]>([]);
    const [ assessmentDescription, setAssessmentDescription ] = useState<DescriptionItem[]>([]);
    const [ tcoLoading, setTcoLoading ] = useState(false);

    const updateAssessment = (assessment: TcoAssessment) => {
        setAssessment(assessment);
        setAssessmentToUpdate(assessment);
    };

    const updateAssessmentToUpdate = (assessment: TcoAssessment) => {
        setAssessmentToUpdate(assessment);
    };

    const updateAssessmentDescription = (description: DescriptionItem[]) => {
        setAssessmentDescription(description);
    };

    const updateAssessmentMetadatas = (metadatas: AssessmentMetadata[]) => {
        setAssessmentMetadatas(metadatas);
    };

    const updateTcoLoading = (updateLoading: boolean) => {
        setTcoLoading(updateLoading);
    };

    return (
        <TcoContext.Provider
            value={{
                assessment,
                assessmentToUpdate,
                assessmentMetadatas,
                assessmentDescription,
                tcoLoading,
                setLocalAssessment: setAssessment,
                updateAssessment,
                updateAssessmentToUpdate,
                updateAssessmentMetadatas,
                updateAssessmentDescription,
                updateTcoLoading,
            }}
        >
            {children}
        </TcoContext.Provider>
    );
};
